import React, { useEffect, useState } from 'react';
import { Settings, X, Phone, Mail, ChevronUp, MessageCircle } from 'lucide-react';
import { Switch } from './ui/switch';
import { company } from '../mock/data';

const DEFAULT_WIDGETS = { contact: true, whatsapp: true, backToTop: true };

const WidgetPanel = () => {
  const [open, setOpen] = useState(false);
  const [widgets, setWidgets] = useState(() => {
    try { return { ...DEFAULT_WIDGETS, ...JSON.parse(localStorage.getItem('lex_widgets') || '{}') }; }
    catch { return DEFAULT_WIDGETS; }
  });
  const [showTop, setShowTop] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => { localStorage.setItem('lex_widgets', JSON.stringify(widgets)); }, [widgets]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggle = (k) => setWidgets((w) => ({ ...w, [k]: !w[k] }));

  return (
    <>
      <div className="fixed right-5 bottom-5 z-50 flex flex-col items-end gap-3">
        {widgets.contact && (
          <div className="relative">
            {contactOpen && (
              <div className="absolute bottom-16 right-0 w-72 bg-white shadow-2xl border border-neutral-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-serif-display font-bold text-black text-lg">Get in touch</h4>
                  <button onClick={() => setContactOpen(false)}><X className="w-4 h-4 text-neutral-500" /></button>
                </div>
                <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-3 p-3 hover:bg-neutral-50">
                  <Phone className="w-4 h-4 text-[#c8102e]" /> <span className="text-sm font-medium">{company.phone}</span>
                </a>
                <a href={`mailto:${company.email}`} className="flex items-center gap-3 p-3 hover:bg-neutral-50">
                  <Mail className="w-4 h-4 text-[#c8102e]" /> <span className="text-sm font-medium">{company.email}</span>
                </a>
              </div>
            )}
            <button onClick={() => setContactOpen(!contactOpen)} className="w-12 h-12 rounded-full bg-[#c8102e] text-white shadow-lg hover:bg-black flex items-center justify-center transition" aria-label="Contact">
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {widgets.whatsapp && (
          <a href={`https://wa.me/${company.phoneRaw.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-[#25D366] text-white shadow-lg hover:brightness-110 flex items-center justify-center transition" aria-label="WhatsApp">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.09.55 4.13 1.6 5.93L2 22l4.34-1.14a9.87 9.87 0 0 0 5.7 1.81h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.86 9.86 0 0 0 12.04 2Zm0 18.15h-.01a8.23 8.23 0 0 1-4.2-1.15l-.3-.18-2.58.68.69-2.51-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.16 8.16 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.12-1.46-.72-1.69-.8-.23-.08-.4-.12-.56.13-.17.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.22-1.45-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.44.12-.14.16-.24.24-.4.08-.16.04-.31-.02-.44-.06-.13-.56-1.36-.77-1.86-.2-.49-.4-.42-.56-.43-.14-.01-.31-.01-.48-.01a.94.94 0 0 0-.68.31c-.23.25-.89.87-.89 2.12s.91 2.46 1.03 2.63c.13.17 1.78 2.72 4.32 3.81.6.26 1.08.41 1.45.53.61.19 1.16.17 1.6.1.49-.07 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.11-.23-.17-.48-.29Z"/></svg>
          </a>
        )}

        {widgets.backToTop && showTop && (
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-11 h-11 rounded-full bg-black text-white shadow-lg hover:bg-[#c8102e] flex items-center justify-center transition" aria-label="Back to top">
            <ChevronUp className="w-5 h-5" />
          </button>
        )}

        <button onClick={() => setOpen(!open)} className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-black text-white shadow-lg flex items-center justify-center transition" aria-label="Customize widgets">
          <Settings className={`w-5 h-5 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="fixed right-5 bottom-24 z-50 w-80 bg-white shadow-2xl border border-neutral-200 overflow-hidden">
          <div className="bg-black text-white p-4 flex items-center justify-between">
            <div>
              <h4 className="font-serif-display font-bold text-lg">Customize widgets</h4>
              <p className="text-xs text-white/60">Toggle what appears on your screen</p>
            </div>
            <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 space-y-4">
            {[
              { key: 'contact', label: 'Contact Widget', desc: 'Call & email shortcuts' },
              { key: 'whatsapp', label: 'WhatsApp Chat', desc: 'Instant messaging' },
              { key: 'backToTop', label: 'Back to Top', desc: 'Scroll-to-top button' },
            ].map((it) => (
              <div key={it.key} className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-black text-sm">{it.label}</div>
                  <div className="text-xs text-neutral-500">{it.desc}</div>
                </div>
                <Switch checked={widgets[it.key]} onCheckedChange={() => toggle(it.key)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default WidgetPanel;
